import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Spinner } from 'react-bootstrap';
import { api } from '../../utils/apiClient';
import { showToast } from '../../utils/toast';
import { ROUTE } from '../../utils/navigation';
import StoreProfileTab from './StoreProfileTab';
import BasicSetupTab from './BasicSetupTab';
import CustomerInterfaceTab, { normalizeLauncherStyle } from './CustomerInterfaceTab';
import EmailTab from './EmailTab';
import DistributionTab from './DistributionTab';
import SettingsTabBar from './SettingsTabBar';
import './settings.css';

const TAB_PATH = {
  'store-profile': ROUTE.SETTINGS_STORE_PROFILE,
  'basic-setup': ROUTE.SETTINGS_BASIC_SETUP,
  'customer-interface': ROUTE.SETTINGS_CUSTOMER_INTERFACE,
  email: ROUTE.SETTINGS_EMAIL,
  distribution: ROUTE.SETTINGS_DISTRIBUTION,
};

const SettingsPage = ({ section = 'store-profile', onNavigate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      if (section === 'distribution') {
        const data = await api.getSettingsSection('distribution');
        setForm({ distributionMode: data.distributionMode || 'load_balanced' });
      } else {
        const data = await api.getSettingsSection(section);
        const key =
          section === 'store-profile'
            ? 'profile'
            : section === 'basic-setup'
              ? 'basicSetup'
              : section === 'customer-interface'
                ? 'customerInterface'
                : 'email';
        const next = data[key] || {};
        if (section === 'customer-interface') {
          setForm({
            ...next,
            widgetStyle: normalizeLauncherStyle(next.widgetStyle),
          });
        } else {
          setForm(next);
        }
      }
    } catch (err) {
      showToast(err.message || t('settings.page.loadFailed'), 'error');
      setForm({});
    } finally {
      setLoading(false);
    }
  }, [section, t]);

  useEffect(() => {
    loadSection();
  }, [loadSection]);

  const save = async () => {
    setSaving(true);
    try {
      if (section === 'distribution') {
        await api.putSettingsSection('distribution', { distributionMode: form.distributionMode });
      } else {
        const payload =
          section === 'customer-interface'
            ? { ...form, widgetStyle: normalizeLauncherStyle(form.widgetStyle) }
            : form;
        const data = await api.putSettingsSection(section, payload);
        if (section === 'email' && data?.email) {
          setForm(data.email);
        }
        if (section === 'customer-interface' && data?.customerInterface) {
          setForm({
            ...data.customerInterface,
            widgetStyle: normalizeLauncherStyle(data.customerInterface.widgetStyle),
          });
        }
      }
      const toast =
        section === 'store-profile' ? t('settings.page.savedProfile') : t('settings.page.saved');
      showToast(toast, 'success');
    } catch (err) {
      showToast(err.message || t('settings.page.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderTab = () => {
    const props = { form, setForm, onNavigate };
    switch (section) {
      case 'store-profile':
        return <StoreProfileTab {...props} />;
      case 'basic-setup':
        return <BasicSetupTab {...props} />;
      case 'customer-interface':
        return <CustomerInterfaceTab {...props} />;
      case 'email':
        return <EmailTab {...props} />;
      case 'distribution':
        return <DistributionTab {...props} />;
      default:
        return null;
    }
  };

  return (
    <div className="py-3 px-2">
      <div className="mb-3">
        <h2 className="h4 mb-1">{t('settings.page.title')}</h2>
        <p className="text-muted mb-0">
          {t('settings.page.subtitle')}
        </p>
      </div>

      <SettingsTabBar
        activeKey={section}
        onSelect={(key) => onNavigate(TAB_PATH[key] || ROUTE.SETTINGS_STORE_PROFILE)}
      />

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          {renderTab()}
          <div className="mt-4 d-flex gap-2">
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? t('settings.page.saving') : t('settings.page.saveChanges')}
            </Button>
            <Button type="button" variant="outline-secondary" onClick={loadSection} disabled={saving}>
              {t('settings.page.reset')}
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default SettingsPage;
